import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import SeoHead from "@/components/SeoHead";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import NotFound from "@/pages/NotFound";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Link } from "react-router-dom";
import NewsArticleBody, { ReadingProgress } from "@/components/news/NewsArticleBody";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { fetchPostBySlug, postRowToArticle } from "@/lib/posts";
import { useLanguage } from "@/i18n/useLanguage";
import { trackEvent } from "@/lib/analytics";

const NewsArticle = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const { localize } = useLanguage();

  const { data: row, isLoading, isError } = useQuery({
    queryKey: ["posts", "slug", slug],
    queryFn: () => fetchPostBySlug(slug!),
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (row) {
      trackEvent("news_article_view", {
        slug: row.slug,
        category: row.category,
        is_external: row.is_external,
      });
    }
  }, [row]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-5 md:px-6 py-12 max-w-3xl">
          <Skeleton className="h-8 w-2/3 mb-6" />
          <Skeleton className="h-64 w-full mb-6 rounded-xl" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-5/6 mb-2" />
          <Skeleton className="h-4 w-4/6" />
        </main>
        <Footer />
      </div>
    );
  }

  if (isError || !row) return <NotFound />;

  const article = postRowToArticle(row, localize(`/news/${row.slug}`));

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.coverImageUrl ?? undefined,
    datePublished: article.date.toISOString(),
    dateModified: article.updatedAt ?? article.date.toISOString(),
    author: article.author
      ? { "@type": "Person", name: article.author }
      : { "@type": "Organization", name: "AI-labbet" },
    publisher: {
      "@type": "Organization",
      name: "AI-labbet",
      url: "https://ailabbet.com",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://ailabbet.com${localize(`/news/${article.slug}`)}`,
    },
    articleSection: article.category,
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: t("breadcrumb.home"), item: `https://ailabbet.com${localize("/")}` },
      { "@type": "ListItem", position: 2, name: t("breadcrumb.news"), item: `https://ailabbet.com${localize("/news")}` },
      { "@type": "ListItem", position: 3, name: article.title },
    ],
  };

  const handleOutbound = () =>
    trackEvent("news_external_outbound_click", {
      slug: article.slug,
      outbound_url: article.externalUrl ?? "",
    });

  return (
    <>
      <SeoHead
        title={article.title}
        description={article.excerpt || article.title}
        path={`/news/${article.slug}`}
        type="article"
        image={article.coverImageUrl ?? undefined}
        jsonLd={[articleJsonLd, breadcrumbJsonLd]}
      />

      <ReadingProgress />

      <div className="min-h-screen bg-background flex flex-col">
        <Header />

        <div className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="container mx-auto px-5 md:px-6 py-3">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={localize("/")} className="text-primary hover:text-primary/80">
                      {t("breadcrumb.home")}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={localize("/news")} className="text-primary hover:text-primary/80">
                      {t("breadcrumb.news")}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-foreground line-clamp-1">{article.title}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </div>

        <main className="flex-1">
          <NewsArticleBody article={article} onExternalClick={handleOutbound} />

          <div className="container mx-auto px-5 md:px-6 max-w-3xl pb-16">
            <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
              <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                {t("newsletter.postCta.heading")}
              </h2>
              <p className="text-muted-foreground mb-5">
                {t("newsletter.postCta.lead")}
              </p>
              <Button asChild size="lg" className="gap-2">
                <Link to={localize("/newsletter")}>
                  {t("newsletter.postCta.button")}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default NewsArticle;
